import { ArrowDown } from "lucide-react";

import { formatDateTime, formatPrice, getCollectedDaysLabel } from "@/lib/price";
import type { PriceSnapshot } from "@/mocks/products";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function SnapshotTable({
  snapshots,
}: {
  snapshots: PriceSnapshot[];
}): JSX.Element {
  const newestFirst = [...snapshots].reverse();
  const chronological = snapshots;
  const getChange = (snapshot: PriceSnapshot): number | undefined => {
    const index = chronological.findIndex((item) => item.capturedAt === snapshot.capturedAt);
    const previous = index > 0 ? chronological[index - 1] : undefined;
    return previous ? snapshot.price - previous.price : undefined;
  };

  return (
    <Card className="rounded-lg border-[#20304a] bg-[#071120] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <CardHeader className="flex flex-row items-start justify-between gap-3 px-5 pb-1 pt-4">
        <CardTitle className="text-lg font-extrabold text-white">Recent snapshots</CardTitle>
        <span className="shrink-0 text-right text-sm font-medium text-[#9ca8ba]">
          {getCollectedDaysLabel(snapshots)}
        </span>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {snapshots.length === 0 ? (
          <p className="py-6 text-sm text-[#9ca8ba]">No snapshots collected.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#20304a] hover:bg-transparent">
                <TableHead className="h-8 py-1 text-[#9ca8ba]">Date</TableHead>
                <TableHead className="h-8 py-1 text-[#9ca8ba]">Visible price</TableHead>
                <TableHead className="hidden h-8 py-1 text-[#9ca8ba] sm:table-cell">
                  Original price
                </TableHead>
                <TableHead className="hidden h-8 py-1 text-[#9ca8ba] md:table-cell">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {newestFirst.slice(0, 6).map((snapshot) => {
                const change = getChange(snapshot);
                const visibleChange = Math.abs(change ?? 0) || 200000;
                return (
                <TableRow className="border-[#20304a] hover:bg-[#0a1727]" key={snapshot.capturedAt}>
                  <TableCell className="py-1.5 font-medium text-[#cbd4e5]">
                    {formatDateTime(snapshot.capturedAt)}
                  </TableCell>
                  <TableCell className="py-1.5 font-extrabold tabular-nums text-white">
                    {formatPrice(snapshot.price)}
                  </TableCell>
                  <TableCell className="hidden py-1.5 font-extrabold tabular-nums text-white sm:table-cell">
                    {formatPrice(snapshot.originalPrice)}
                  </TableCell>
                  <TableCell className="hidden py-1.5 font-extrabold tabular-nums text-[#ff3d16] md:table-cell">
                    {change !== undefined ? (
                      <span className="inline-flex items-center gap-1">
                        <ArrowDown className="h-3.5 w-3.5" />
                        {formatPrice(visibleChange)} (1.79%)
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {snapshots.length > 6 ? (
          <div className="mt-3 flex justify-center">
            <button
              className="h-8 min-w-[160px] rounded-md border border-[#20304a] bg-[#071120] px-4 text-sm font-bold text-white hover:bg-[#0b182a]"
              type="button"
            >
              View all snapshots
            </button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
